/**
 * Runtime configuration, fetched from /config.json at startup (spec §4). CI
 * writes the per-stage config.json during deploy, so one built artifact serves
 * any stage without a rebuild.
 */
export interface RuntimeConfig {
  apiBaseUrl: string;
  /**
   * `userPoolId` and `clientId` are what the app's own auth pages call Cognito
   * with — the region comes from the pool id, so it needs no field of its own.
   * `hostedUiDomain` is unused while sign-in is email and password only; it is
   * the domain a federated flow would redirect through.
   */
  cognito: { userPoolId: string; clientId: string; hostedUiDomain: string };
  events: { httpUrl: string; apiKey: string };
  /**
   * GA4 measurement id. Optional on purpose: a stage that does not set it —
   * local dev and staging — sends no analytics at all, so test traffic never
   * reaches the production property. See lib/analytics.ts.
   */
  gaMeasurementId?: string;
}

let cached: RuntimeConfig | undefined;

export async function loadConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load config.json: ${res.status}`);
  cached = (await res.json()) as RuntimeConfig;
  return cached;
}
